import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  decryptString,
  deriveWrappingKey,
  encryptString,
  exportDataKey,
  fromBase64,
  generateDataKey,
  importDataKey,
  randomBytes,
  toBase64,
  unwrapDataKey,
  wrapDataKey,
} from "@/lib/e2ee";

export type VaultStatus = "loading" | "needs-setup" | "locked" | "unlocked" | "skipped";

export interface EncryptedConversationMeta {
  id: string;
  title: string;
  messageCount: number;
  updatedAt: string;
}

const SESSION_KEY = "mindful-vault-key";
const SKIP_KEY = "mindful-vault-skipped";

export function useMindfulVault() {
  const [status, setStatus] = useState<VaultStatus>("loading");
  const [conversations, setConversations] = useState<EncryptedConversationMeta[]>([]);
  const dataKeyRef = useRef<CryptoKey | null>(null);
  const [keyVersion, setKeyVersion] = useState(0);

  const loadConversations = useCallback(async (key: CryptoKey) => {
    const { data, error } = await supabase
      .from("mindful_conversations")
      .select("id, title_ciphertext, title_iv, message_count, updated_at")
      .order("updated_at", { ascending: false });
    if (error || !data) return;

    const items: EncryptedConversationMeta[] = [];
    for (const row of data) {
      let title = "Conversation";
      if (row.title_ciphertext && row.title_iv) {
        try {
          title = await decryptString(row.title_ciphertext, row.title_iv, key);
        } catch {
          title = "Conversation";
        }
      }
      items.push({
        id: row.id,
        title,
        messageCount: row.message_count ?? 0,
        updatedAt: row.updated_at,
      });
    }
    setConversations(items);
  }, []);

  const activateKey = useCallback(
    async (key: CryptoKey, cache = true) => {
      dataKeyRef.current = key;
      if (cache) {
        try {
          sessionStorage.setItem(SESSION_KEY, toBase64(await exportDataKey(key)));
        } catch {
          /* ignore */
        }
      }
      setKeyVersion((v) => v + 1);
      setStatus("unlocked");
      await loadConversations(key);
    },
    [loadConversations]
  );

  // Bootstrap: does the user have a vault, and is a key cached for this session?
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        if (!cancelled) setStatus("skipped");
        return;
      }
      const { data } = await supabase
        .from("mindful_encryption_keys")
        .select("user_id")
        .maybeSingle();
      if (cancelled) return;

      if (!data) {
        setStatus(localStorage.getItem(SKIP_KEY) === "true" ? "skipped" : "needs-setup");
        return;
      }

      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) {
        try {
          const key = await importDataKey(fromBase64(cached));
          if (!cancelled) await activateKey(key, false);
          return;
        } catch {
          sessionStorage.removeItem(SESSION_KEY);
        }
      }
      if (!cancelled) setStatus("locked");
    })();
    return () => {
      cancelled = true;
    };
  }, [activateKey]);

  const setupVault = useCallback(
    async (passphrase: string) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) throw new Error("You need to be signed in.");

      const salt = randomBytes(16);
      const wrappingKey = await deriveWrappingKey(passphrase, salt);
      const dataKey = await generateDataKey();
      const { wrapped, iv } = await wrapDataKey(dataKey, wrappingKey);

      const { error } = await supabase.from("mindful_encryption_keys").upsert(
        {
          user_id: userId,
          kdf_salt: toBase64(salt),
          wrapped_data_key: wrapped,
          wrap_iv: iv,
        },
        { onConflict: "user_id" }
      );
      if (error) throw new Error(error.message);

      localStorage.removeItem(SKIP_KEY);
      await activateKey(dataKey);
    },
    [activateKey]
  );

  const unlockVault = useCallback(
    async (passphrase: string) => {
      const { data, error } = await supabase
        .from("mindful_encryption_keys")
        .select("kdf_salt, wrapped_data_key, wrap_iv")
        .maybeSingle();
      if (error || !data) throw new Error("Could not load your private key.");

      const wrappingKey = await deriveWrappingKey(passphrase, fromBase64(data.kdf_salt));
      let dataKey: CryptoKey;
      try {
        dataKey = await unwrapDataKey(data.wrapped_data_key, data.wrap_iv, wrappingKey);
      } catch {
        throw new Error("That passphrase does not match.");
      }
      await activateKey(dataKey);
    },
    [activateKey]
  );

  const lockVault = useCallback(() => {
    dataKeyRef.current = null;
    sessionStorage.removeItem(SESSION_KEY);
    setConversations([]);
    setKeyVersion((v) => v + 1);
    setStatus("locked");
  }, []);

  const skipVault = useCallback(() => {
    localStorage.setItem(SKIP_KEY, "true");
    setStatus("skipped");
  }, []);

  /** Wipe all stored history and start over with a new passphrase. */
  const resetVault = useCallback(
    async (passphrase: string) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) throw new Error("You need to be signed in.");
      await supabase.from("mindful_conversations").delete().eq("user_id", userId);
      sessionStorage.removeItem(SESSION_KEY);
      setConversations([]);
      await setupVault(passphrase);
    },
    [setupVault]
  );

  const saveConversation = useCallback(
    async (payload: {
      id?: string;
      messages: unknown;
      title: string;
      messageCount: number;
    }): Promise<string | null> => {
      const key = dataKeyRef.current;
      if (!key) return null;
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) return null;

      const body = await encryptString(JSON.stringify(payload.messages), key);
      const title = await encryptString(payload.title, key);

      if (payload.id) {
        const { error } = await supabase
          .from("mindful_conversations")
          .update({
            ciphertext: body.ciphertext,
            iv: body.iv,
            title_ciphertext: title.ciphertext,
            title_iv: title.iv,
            message_count: payload.messageCount,
          })
          .eq("id", payload.id);
        if (error) {
          console.error("Failed to save conversation:", error.message);
          return payload.id;
        }
        await loadConversations(key);
        return payload.id;
      }

      const { data, error } = await supabase
        .from("mindful_conversations")
        .insert({
          user_id: userId,
          ciphertext: body.ciphertext,
          iv: body.iv,
          title_ciphertext: title.ciphertext,
          title_iv: title.iv,
          message_count: payload.messageCount,
        })
        .select("id")
        .single();
      if (error || !data) {
        console.error("Failed to save conversation:", error?.message);
        return null;
      }
      await loadConversations(key);
      return data.id;
    },
    [loadConversations]
  );

  const loadConversation = useCallback(async (id: string): Promise<unknown | null> => {
    const key = dataKeyRef.current;
    if (!key) return null;
    const { data, error } = await supabase
      .from("mindful_conversations")
      .select("ciphertext, iv")
      .eq("id", id)
      .maybeSingle();
    if (error || !data) return null;
    try {
      return JSON.parse(await decryptString(data.ciphertext, data.iv, key));
    } catch {
      return null;
    }
  }, []);

  const deleteConversation = useCallback(
    async (id: string) => {
      await supabase.from("mindful_conversations").delete().eq("id", id);
      const key = dataKeyRef.current;
      if (key) await loadConversations(key);
    },
    [loadConversations]
  );

  return {
    status,
    conversations,
    hasKey: dataKeyRef.current !== null,
    keyVersion,
    setupVault,
    unlockVault,
    lockVault,
    skipVault,
    resetVault,
    saveConversation,
    loadConversation,
    deleteConversation,
  };
}
